import { datadogRum } from "@datadog/browser-rum";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { colors, pxToRem } from "theme";

import { useUploadPhotos } from "pages/VehicleAnalysis/hooks/useUploadPhotos";

import BottomDrawer from "components/BottomDrawer";
import ClientTranslation from "components/ClientTranslation";
import Feedback from "components/Feedback";
import Flex from "components/Flex";
import { ErrorIcon } from "components/Icons/Error";
import { NoInternetIcon } from "components/Icons/NoInternet";
import Loader from "components/Loader";
import PageLayout from "components/PageLayout";
import Text from "components/Text";

import { classifyPhotos } from "api/classifyPhotos";
import { clientConfigAtom } from "atoms/clientConfig";
import { clientIdAtom } from "atoms/clientId";
import { photosClassifiedAtom } from "atoms/photosUpload";
import { serverSessionAtom } from "atoms/serverSession";
import { sessionIdAtom } from "atoms/sessionId";
import { loaderTranslationKeyAtom } from "atoms/vehicleAnalysis";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useCompleteSession } from "hooks/useCompleteSession";
import { usePageTracking } from "hooks/usePageTracking";
import { generateClassifyBody } from "utils/generateClassifyBody/generateClassifyBody";

import { useDigitalJourneyInformation } from "./hooks/useDigitalJourneyInformation";

import client from 'api/client';
import { signatureImageAtom } from "atoms/signature";
import { set } from "optics-ts";


export const DJFlowCompletedPage = () => {
  const { t } = useClientTranslation();
  const sessionId = useAtomValue(sessionIdAtom);
  const clientId = useAtomValue(clientIdAtom);
  const [photosClassified, setPhotosClassified] = useAtom(photosClassifiedAtom);
  const config = useAtomValue(clientConfigAtom);
  const loaderTranslationKey = useAtomValue(loaderTranslationKeyAtom);
  const serverSession = useAtomValue(serverSessionAtom);
  const [hasCompleted, setHasCompleted] = useState(false);
  const [signatureImage, setSignatureImage] = useAtom(signatureImageAtom);
  usePageTracking({ name: "Digital Journey Done - view" });


  const generatePdf = useMutation(
    () => client.post(`/client/${clientId}/session/${sessionId}/generatePdf`),
    {
      onError: (error) => {
        datadogRum.addError(error, { message: 'PDF generation failed' });
      }
    }
  );

  const [pdfRetries, setPdfRetries] = useState(0);
  const MAX_PDF_RETRIES = 3;

  useEffect(() => {
    // Trigger PDF generation when session is completed and signature exists. Up to number of retries if it fails.
    if (hasCompleted && !generatePdf.isSuccess && !generatePdf.isLoading && signatureImage) {
      if (pdfRetries < MAX_PDF_RETRIES) {
        generatePdf.mutate();
        setSignatureImage(null); // It needs to be cleared here because this indicates if a PDF needs to be generated otherwise in every rerender it is going to be lost
      } else {
        setSignatureImage(null); // If it reaches max retries.
      }
    }
  }, [hasCompleted, generatePdf, signatureImage, pdfRetries, setSignatureImage]);

  useEffect(() => {
    if (generatePdf.isError && pdfRetries < MAX_PDF_RETRIES) {
      const retryTimeout = setTimeout(() => {
        setPdfRetries(pdfRetries + 1);
      }, 1000);
      return () => clearTimeout(retryTimeout);
    }
  }, [generatePdf.isError, pdfRetries]);

  const { completeSession, completeSessionError } = useCompleteSession({
    onSuccess: () => {},
  });

  const { uploadAllImages, photoUploadError, photoUploadComplete } = useUploadPhotos();

  const classifyBody = useDigitalJourneyInformation();

  const { mutate: postPhotosForClassification, error: photoClassificationError } = useMutation(
    () => classifyPhotos(sessionId, clientId, generateClassifyBody(classifyBody, config)),
    {
      onSuccess: () => {
        setPhotosClassified(true);
      },
      onError: (error) => {
        datadogRum.addError(error, {
          message: "Failed to classify session",
        });
      },
    }
  );

  useEffect(() => {
    if (serverSession && !serverSession.completedAt) {
      if (!photoUploadComplete) uploadAllImages();
      if (!photosClassified && photoUploadComplete) postPhotosForClassification();
      if (!photoClassificationError && photosClassified) completeSession();
    }
    if (serverSession && serverSession.completedAt) {
      setHasCompleted(true);
    }
  }, [
    completeSession,
    photosClassified,
    postPhotosForClassification,
    photoUploadComplete,
    photoClassificationError,
    serverSession,
  ]);

  const retryFailedRequest = () => {
    if (photoUploadError) uploadAllImages();

    if (!!photoClassificationError) postPhotosForClassification();

    if (!!completeSessionError) completeSession();
  };

  return (
    <PageLayout
      contentSpacing="space-between"
      title={hasCompleted ? "" : t("vehicle_analysis.header")}
      dataTestId={hasCompleted ? "djFlowCompleted" : "djVehicleAnalysis"}
    >
      {hasCompleted ? (
        <Feedback visible={true} showFeedbackText={false}>
          <Flex px={24}>
            <Flex
              align="center"
              sx={{
                "& > img": {
                  width: pxToRem[192],
                  height: pxToRem[192],
                },
              }}
            >
              <img src="/assets/png/digitalJourney/done-image.png" alt="Digital Journey Session Done" />
            </Flex>
            <Flex gap={10}>
              <Text
                weight={700}
                sizeVariant="lg"
                sx={{
                  "&": {
                    textAlign: "center",
                  },
                }}
                mt={24}
              >
                La denuncia è stata inviata correttamente
              </Text>
              <Text weight={700}>Cosa succede ora?</Text>
              <ul style={{ margin: 0, paddingLeft: "1.5rem", listStyleType: "disc" }}>
                <li style={{ display: "list-item" }}><Text>A breve il nostro team verificherà la tua pratica;</Text></li>
                <li style={{ display: "list-item" }}>
                  <Text>
                    Entro tre giorni lavorativi a partire da oggi, riceverai una <strong>nostra mail</strong> con la{" "}
                    <strong>conferma di apertura del Sinistro;</strong>
                  </Text>
                </li>
                <li style={{ display: "list-item" }}><Text>Se avremo bisogno di ulteriori dettagli, ti contatteremo noi.</Text></li>
              </ul>
              <Text>
                <strong>Attendi il nostro riscontro;</strong> ci aiuterà a gestire la tua richiesta nel minor tempo possibile.
              </Text>
              <Text mt={30}>
                <strong>Com'è andata la compilazione?{" "}
                <a
                  href="https://www.conte.it/nps-denuncia-sx/"
                  style={{ color: colors.primary }}
                  target="_blank"
                  rel="noreferrer"
                >
                  Clicca qui
                </a></strong>{" "}
                per farcelo sapere.
              </Text>
            </Flex>
          </Flex>
        </Feedback>
      ) : (
        <>
          <Flex py={16} px={24} gap={16} style={{ height: "100%" }}>
            <Text sizeVariant="md">{t("vehicle_analysis.instruction")}</Text>
            <Text sizeVariant="md">
              <ClientTranslation i18nKey="vehicle_analysis.stay">{t("vehicle_analysis.stay")}</ClientTranslation>
            </Text>
            <Loader message={t(loaderTranslationKey)} />
          </Flex>
          <BottomDrawer
            isOpen={photoUploadError || !!photoClassificationError || !!completeSessionError}
            closeOnClickOutside={false}
            heading={navigator.onLine ? t("offline.error.header") : t("offline.header")}
            headingIcon={navigator.onLine ? <ErrorIcon /> : <NoInternetIcon />}
            callToActionText={t("offline.error.retry_uploading")}
            callToActionOnClick={retryFailedRequest}
          >
            <Text sizeVariant="md">{navigator.onLine ? t("offline.error.instruction") : t("offline.instruction")}</Text>
          </BottomDrawer>
        </>
      )}
    </PageLayout>
  );
};
